<script lang="ts">
  import SettingsService from '@/services/settings.service'
  import store from '@/store'
  import { defineComponent, provide, watch } from 'vue'
  import { useSettings } from '@/composables/useSettings'
  import { useSession } from '@/composables/useSession'
  import SessionExpiryDialog from '@/components/SessionExpiryDialog.vue'
  import ToastContainer from '@/components/ToastContainer.vue'

  export default defineComponent({
    components: { SessionExpiryDialog, ToastContainer },

    setup() {
      const {
        hasCustomProperties,
        hasIssuedMaterials,
        lastImportDate,
        refresh,
        appName,
      } = useSettings()

      provide('updateNavigation', async () => {
        await refresh()
      })

      const { secondsRemaining, showCountdown, hasExpired } = useSession()

      // Nothing requests anything while a user sits idle, so the client has to
      // act on zero itself rather than waiting for a 419 that will not arrive
      // until they touch something.
      watch(hasExpired, (expired) => {
        if (!expired) return

        try {
          localStorage.setItem('signOutReason', 'sessionExpired')
        } catch {
          // Blocked storage only costs the explanation, not the sign-out.
        }

        store.commit({ type: 'setAuthenticatedUser', authenticatedUser: false })
        store.commit({ type: 'setAccessToken', accessToken: '' })

        location.reload()
      })

      return {
        secondsRemaining,
        showCountdown,
        hasCustomProperties,
        hasIssuedMaterials,
        lastImportDate,
        appName,
      }
    },
    data() {
      return {
        isMenuOpen: false,
        isLoading: true,
        version: process.env.PACKAGE_VERSION,
        apiVersion: null as string | null,
      }
    },
    computed: {
      // The store is the single source of truth. This used to be mirrored into a
      // local flag driven by a DOM event that was never unregistered and needed
      // $forceUpdate to land.
      isAuthenticated(): boolean {
        return !!this.$store.state.authenticatedUser
      },

      userName(): string {
        return this.$store.state.authenticatedUser?.name ?? ''
      },
      userInitials(): string {
        const parts = this.userName.trim().split(/\s+/).filter(Boolean)

        if (parts.length === 0) return ''

        const first = parts[0][0]
        const last = parts.length > 1 ? parts[parts.length - 1][0] : ''

        return (first + last).toUpperCase()
      },
    },
    async mounted() {
      const html = document.documentElement

      await new SettingsService().getAll().then((response) => {
        // Absent on an API deployed before versioning existed; the footer
        // simply omits it in that case.
        this.apiVersion = response.data?.version ?? null

        store.commit({
          type: 'setModules',
          modules: response.data.modules,
        })

        store.commit({
          type: 'setBasicOptIn',
          basicOptIn: response.data?.basicOptIn ?? false,
        })

        store.commit({
          type: 'setMultiLanguage',
          multiLanguage: response.data?.multiLanguage ?? false,
        })

        store.commit({
          type: 'setIssuedMaterials',
          issuedMaterials: response.data?.issuedMaterials ?? false,
        })

        store.commit({
          type: 'setInteractionNote',
          interactionNote: response.data?.interactionNote ?? true,
        })

        store.commit({
          type: 'setClientNote',
          clientNote: response.data?.clientNote ?? true,
        })

        this.isLoading = false
      })

      html.setAttribute('lang', this.$i18n.locale)
    },

    methods: {
      hideMenu() {
        this.isMenuOpen = false
      },
      toggleMenu() {
        this.isMenuOpen = !this.isMenuOpen
      },
    },
  })
</script>

<template>
  <div v-if="!isLoading" id="app" :class="{ 'app--auth': !isAuthenticated }">
    <nav v-if="isAuthenticated" class="top-bar">
      <RouterLink to="/">
        <img class="interact-logo" src="/img/arrows.png" />
        <div class="branding">
          <template v-if="appName">
            {{ appName }}
          </template>
          <template v-else> InterAct </template>
        </div>
      </RouterLink>

      <div class="menu-toggle" @click="toggleMenu()">Menu</div>

      <!-- Quiet until the last few minutes: a clock counting down to something
           bad, visible all day, stops being seen. -->
      <div v-if="showCountdown" class="session-countdown" aria-live="polite">
        {{ $t('session.endsIn') }}
        {{ Math.floor((secondsRemaining || 0) / 60) }}:{{
          String((secondsRemaining || 0) % 60).padStart(2, '0')
        }}
      </div>

      <div class="account">
        <RouterLink to="/account" class="account__link">
          <span v-if="userName" class="account__name">{{ userName }}</span>

          <span class="account__avatar">
            <template v-if="userInitials">{{ userInitials }}</template>
            <IconUser v-else />
          </span>
        </RouterLink>
      </div>
    </nav>

    <div class="main" :class="{ 'main--bare': !isAuthenticated }">
      <nav
        v-if="isAuthenticated"
        class="menu"
        :class="{ 'is-open': isMenuOpen }"
        @click="hideMenu()"
      >
        <RouterLink to="/" class="branding-block">
          <img class="interact-logo" src="/img/arrows-white.png" />
          <template v-if="appName">
            {{ appName }}
          </template>
          <template v-else> InterAct </template>
        </RouterLink>

        <RouterLink to="/">
          {{ $t('menu.dashboard') }}
        </RouterLink>

        <RouterLink to="/klanten">
          {{ $t('menu.clients') }}
        </RouterLink>

        <RouterLink to="/locaties">
          {{ $t('menu.locations') }}
        </RouterLink>

        <RouterLink to="/interacties">
          {{ $t('menu.interactions') }}
        </RouterLink>

        <RouterLink v-if="can('edit products')" to="/producten">
          {{ $t('menu.products') }}
        </RouterLink>

        <RouterLink
          v-if="
            hasCustomProperties &&
            (is('Super Admin') || can('manage properties'))
          "
          to="/eigenschappen"
        >
          {{ $t('menu.properties') }}
        </RouterLink>

        <RouterLink v-if="hasIssuedMaterials" to="/afgegeven-materiaal">
          {{ $t('menu.issuedMaterial') }}
        </RouterLink>

        <RouterLink
          v-if="hasModule('omnichannel')"
          to="/omnichannel/email-templates"
        >
          {{ $t('menu.emailTemplates') }}
        </RouterLink>

        <RouterLink v-if="hasModule('omnichannel')" to="/omnichannel/emails">
          {{ $t('menu.emails') }}
        </RouterLink>

        <RouterLink v-if="can('see rayons')" to="/reps">
          {{ $t('menu.reps') }}
        </RouterLink>

        <template v-if="hasModule('activity')">
          <RouterLink to="/dagbesteding">
            {{ $t('menu.activities') }}
          </RouterLink>
        </template>

        <RouterLink v-if="is('Super Admin')" to="/admin">
          {{ $t('menu.adminPanel') }}
        </RouterLink>

        <div class="version">
          <div v-if="version">
            {{ $t('generic.version') }}: {{ version
            }}<template v-if="apiVersion"> (API {{ apiVersion }})</template>
          </div>

          <div v-if="lastImportDate">
            {{ $t('generic.lastImport') }}: {{ lastImportDate }}
          </div>
        </div>
      </nav>

      <!-- Only inside the authenticated branch, so it can never appear over the
           sign-in or two-factor screens, where it would be nonsense. -->
      <SessionExpiryDialog v-if="isAuthenticated" />

      <div v-if="isAuthenticated" class="pusher"></div>

      <div class="content">
        <RouterView />
      </div>
    </div>

    <ToastContainer />
  </div>
</template>

<style lang="scss">
  @use '@/variables' as *;
  @use 'bootstrap/scss/bootstrap-grid' with (
    $grid-gutter-width: 1.5rem
  );

  html {
    box-sizing: border-box;
    font-size: 14.5px;
  }

  *,
  *:before,
  *:after {
    box-sizing: inherit;
  }

  :where(
    input:not([type='checkbox'], [type='radio'], .vs__search),
    select,
    textarea
  ):focus {
    outline: 0;
    border-color: $primary-color;
    box-shadow: $focus-ring;
  }

  // Lucide icons inherit the surrounding text color and size, mirroring the
  // Font Awesome glyphs they replaced. Lucide art sits inside a 24×24 viewBox
  // with an inset, so it needs to run a touch larger than 1em to match the
  // visual weight of the old solid FA glyphs.
  .lucide-icon {
    width: 1.15em;
    height: 1.15em;
    vertical-align: -0.2em;
  }

  // Icon-only buttons are a fixed 33×33 square, so size the glyph in absolute
  // units instead of `em` — the button font-size also drives text buttons
  // (e.g. pagination page numbers) and must stay small.
  .button--icon .lucide-icon {
    width: 1.1rem;
    height: 1.1rem;
    // The SVG is a flex child of the button; without this it shrinks to the
    // available space instead of honouring the width/height above.
    flex-shrink: 0;
  }

  body {
    font-family: 'Nunito', sans-serif;
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    margin: 0;
    font-size: 1rem;
    color: $primary-font-color;
    background: $surface-muted;
    background: linear-gradient(47deg, $neutral-50 0%, $neutral-200 49%);
    overflow-x: hidden;
  }

  header {
    overflow: hidden;
  }

  .ce-settings__plugin-zone {
    display: none;
  }

  .row {
    .card {
      height: calc(100% - 1.5rem);
    }
  }

  .interact-logo {
    height: 3.75rem;
    padding: 0.75rem 0;
    margin-right: 0.25rem;
  }

  .branding {
    line-height: 3.75rem;
    display: inline-block;
    float: right;
    margin-left: 0.25rem;
    font-weight: 700;
  }

  .session-countdown {
    display: flex;
    align-items: center;
    margin-left: auto;
    padding: 0.25rem 0.6rem;
    border-radius: $radius-pill;
    background-color: $color-warning-tint;
    color: darken($color-warning, 20%);
    font-size: $font-size-xs;
    font-weight: $font-weight-bold;
    font-variant-numeric: tabular-nums;
  }

  .content {
    max-width: calc(100vw - 15rem);

    @media (max-width: $breakpoint) {
      max-width: 100vw;
    }
  }

  .main {
    display: flex;
    min-height: calc(100vh - 3.75rem);

    nav.menu,
    .content {
      display: flex;
      flex-direction: column;
    }

    .content {
      padding: 2rem 2rem 3rem 2rem;
      flex-grow: 1;

      h1 {
        line-height: 2rem;
        margin: 0 0 1.5rem 0;
      }

      @media (max-width: $breakpoint) {
        padding: 1.5rem 1.5rem 1.5rem 1.5rem;
      }
    }
  }

  // Login and two-factor bring their own full-bleed layout, so the chrome's
  // gutters, width cap and top-bar offset have to get out of the way.
  #app.app--auth {
    .main {
      min-height: 100vh;
    }

    .content {
      max-width: 100vw;
      padding: 0;
    }
  }

  nav {
    z-index: 9000;

    &.top-bar {
      display: flex;
      align-items: center;
      background-color: $surface;
      width: 100%;
      height: 3.75rem;
      box-shadow: $shadow-top-bar;
      padding: 0 1.5rem;

      a {
        text-decoration: none;
        color: $primary-font-color;
      }

      .branding {
        font-size: 1.1rem;
        font-weight: 700;
      }
    }

    &.menu {
      position: fixed;
      top: 0;
      left: 0;
      height: 100vh;
      width: 15rem;
      min-width: 15rem;
      background-color: $primary-color;
      background-image: linear-gradient(
        180deg,
        $primary-color 10%,
        $primary-color-dark-fade 100%
      );
      list-style: none;

      .branding-block {
        background-color: $primary-color-dark-fade;
        height: 3.75rem;
        margin-bottom: 1rem;
      }

      .version {
        position: absolute;
        bottom: 1rem;
        text-align: center;
        font-size: 0.8rem;
        width: 100%;
        color: $neutral-0;
      }

      a {
        position: relative;
        color: $neutral-0;
        text-decoration: none;
        display: flex;
        align-items: center;
        min-height: 3rem;
        padding: 1rem 2rem;
        transition: all 0.25s ease-in-out;

        &:not(.branding-block):not(:last-child):after {
          content: '';
          height: 1px;
          background-color: rgba($neutral-0, 0.1);
          width: 75%;
          position: absolute;
          bottom: 0;
        }

        &:hover {
          cursor: pointer;
          opacity: 0.7;
        }
      }

      @media (max-width: $breakpoint) {
        left: -15rem;
        opacity: 0;
        transition: all 0.25s ease-in-out;

        &.is-open {
          left: 0;
          opacity: 1;
        }
      }
    }
  }

  .account {
    display: flex;
    align-self: center;
    align-items: center;
    margin-left: auto;

    @media (max-width: $breakpoint) {
      margin-left: 1rem;
    }

    &__link {
      display: flex;
      align-items: center;
      gap: 0.75rem;
      text-decoration: none;
    }

    &__avatar {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 2.25rem;
      height: 2.25rem;
      border-radius: 50%;
      background-color: $primary-color;
      color: $neutral-0 !important;
      font-size: 0.85rem;
      font-weight: 700;
      line-height: 1;
      transition: 0.25s all ease-in-out;
    }

    &__name {
      color: $text-muted;
      font-weight: 500;
      font-size: 1rem;
      white-space: nowrap;

      @media (max-width: $breakpoint) {
        display: none;
      }
    }

    &:hover &__avatar {
      opacity: 0.75;
    }
  }

  .pusher {
    width: 15rem;

    @media (max-width: $breakpoint) {
      display: none;
    }
  }

  .menu-toggle {
    display: none;

    @media (max-width: $breakpoint) {
      display: flex;
      align-self: center;
      margin-left: auto;

      &:hover {
        cursor: pointer;
      }
    }
  }

  .form-group,
  .filter {
    input:not([type='checkbox']):not([type='radio']):not(.vs__search),
    select,
    textarea {
      width: 100%;
      font-family: $font-family-base;
      font-size: $font-size-2xs;
      color: $text-color;
      background-color: $surface;
      border: 1px solid $border-color-strong;
      border-radius: $default-border-radius;
      outline: 0;
      transition:
        border-color $transition-fast,
        box-shadow $transition-fast;

      &:focus {
        border-color: $primary-color;
      }

      &:disabled {
        background-color: $neutral-100;
        color: $text-muted;
        cursor: not-allowed;
      }
    }

    input:not([type='checkbox']):not([type='radio']):not(.vs__search),
    select {
      height: 2.5rem;
      padding: 0 $space-3;
    }

    select {
      cursor: pointer;
    }

    textarea {
      padding: $space-3;
    }
  }
</style>
